"use client";

import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/context/lang";
import { Logo } from "@/components/logo";

export default function TermsPage() {
  const { T, lang } = useLang();

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center gap-4 px-6 py-4">
          <Link href="/" className="flex items-center gap-2 text-neutral-500 hover:text-cyan-600 transition-colors">
            <ArrowLeft size={18} />
            <span className="text-sm">{lang === "tr" ? "Ana Sayfa" : "Home"}</span>
          </Link>
          <div className="ml-auto">
            <Logo size="sm" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm p-8 md:p-12">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">Terms of Service</h1>
          <p className="text-sm text-neutral-400 mb-8">Last updated: April 7, 2026</p>

          <div className="prose prose-neutral max-w-none text-neutral-600 space-y-6 text-[15px] leading-relaxed">

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">1. Acceptance of Terms</h2>
              <p>
                These Terms of Service (&quot;Terms&quot;) constitute a legally binding agreement between you (&quot;User,&quot;
                &quot;you,&quot; or &quot;your&quot;) and <strong>CreateNano LLC</strong>, a limited liability company organized under the
                laws of the State of New Mexico, USA (&quot;Company,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), governing your access
                to and use of the SpeakRoar platform (&quot;Platform&quot;), including all related websites, applications,
                and services.
              </p>
              <p>
                By accessing, registering for, or using the Platform in any manner, you acknowledge that you have
                read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, you
                must not access or use the Platform.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">2. Service Description</h2>
              <p>SpeakRoar is an AI-powered text-to-speech platform that enables users to generate synthetic voice audio. The Platform provides the following services:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>AI-driven text-to-speech voice generation</li>
                <li>Access to 2,600+ voice models across 25+ languages</li>
                <li>Built-in audio editing tools</li>
                <li>Downloading of generated audio files</li>
                <li>A credit-based usage system</li>
              </ul>
              <p className="mt-2">
                We reserve the right to modify, expand, or discontinue any features, functionality, or technical
                infrastructure of the Platform at any time, with or without prior notice.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">3. Account Registration</h2>
              <p>
                To use the Platform, you must create an account using a valid email address. By registering,
                you agree to the following:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>You will provide accurate, current, and complete information during registration</li>
                <li>You are solely responsible for maintaining the confidentiality and security of your account credentials</li>
                <li>You are responsible for all activities that occur under your account</li>
                <li>You will promptly notify us of any unauthorized use of your account</li>
                <li>You are at least 18 years of age, or you have obtained the consent of a parent or legal guardian</li>
              </ul>
              <p className="mt-2">
                We reserve the right to suspend, restrict, or permanently terminate any user account at any time,
                for any reason, with or without notice.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">4. Credit System &amp; Payments</h2>
              <p>
                The Platform operates on a credit-based usage system. Voice generation and other services
                consume a specified number of credits per use.
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Credits are purchased through the Platform&apos;s payment page</li>
                <li>Credit pricing is subject to change at any time without prior notice</li>
                <li><strong>All credit purchases are final and non-refundable. No refunds will be issued for purchased credits under any circumstances</strong></li>
                <li>Unused credits may be subject to an expiration date</li>
                <li>In the event of account termination or closure, unused credits will not be refunded</li>
                <li>We reserve the right to modify credit amounts, pricing, and consumption rates at any time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">5. Acceptable Use Policy</h2>
              <p>By using the Platform, you agree not to:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Generate content that is unlawful, obscene, defamatory, threatening, or that constitutes hate speech or violates the rights of any third party</li>
                <li>Use the Platform for fraud, identity theft, impersonation, or any deceptive purposes</li>
                <li>Attempt to damage, disrupt, or reverse-engineer the Platform&apos;s technical infrastructure</li>
                <li>Access the Platform using automated tools, bots, scrapers, or similar mechanisms without our express written consent</li>
                <li>Use generated voices to impersonate real individuals without their explicit consent</li>
                <li>Access or use another user&apos;s account without authorization</li>
                <li>Resell or redistribute the Platform&apos;s services to third parties unless expressly permitted</li>
                <li>Use the Platform to generate content that violates any applicable federal, state, or local laws</li>
              </ul>
              <p className="mt-2">
                Violation of this Acceptable Use Policy may result in immediate suspension or permanent
                termination of your account without prior warning. No refund of unused credits will be provided
                in such cases.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">6. Intellectual Property</h2>
              <p>
                The Platform&apos;s design, software code, logos, graphics, trademarks, and all associated content
                are the intellectual property of CreateNano LLC. You agree that:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>You may use audio files generated through the Platform for your personal and commercial projects</li>
                <li>You may not copy, reproduce, distribute, or create derivative works of the Platform itself, its source code, or its design</li>
                <li>You may not use the Company&apos;s brand, logo, or trade identity without our prior written consent</li>
              </ul>
              <p className="mt-2">
                You are solely responsible for the legality of any text you input into the Platform and for ensuring
                that your use of generated content does not infringe upon any third-party intellectual property rights.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">7. Service Availability &amp; Disclaimers</h2>
              <p>
                We do not guarantee that the Platform will be uninterrupted, error-free, or secure at all times.
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>The Platform may experience temporary outages due to maintenance, updates, or technical issues</li>
                <li>We reserve the right to suspend or discontinue the Platform at any time, temporarily or permanently, for any reason</li>
                <li>No compensation or refund will be provided for service interruptions</li>
                <li>We are not responsible for disruptions caused by third-party service providers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">8. Limitation of Liability</h2>
              <p>
                <strong>THE PLATFORM IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND,
                EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
                FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</strong>
              </p>
              <p>TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>CreateNano LLC shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use the Platform</li>
                <li>We shall not be liable for any loss of data, revenue, profits, business opportunities, goodwill, or other intangible losses</li>
                <li>We make no warranty regarding the quality, accuracy, or suitability of generated audio content</li>
                <li>We are not responsible for the performance or changes of third-party AI services integrated into the Platform</li>
                <li>We shall not be liable for damages arising from security breaches, unauthorized access, or cyberattacks</li>
              </ul>
              <p className="mt-2">
                You acknowledge and agree that you use the Platform entirely at your own risk. In no event shall
                our total aggregate liability exceed the amount you paid to us in the one (1) month immediately
                preceding the event giving rise to the claim.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">9. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless CreateNano LLC, its members, managers, officers,
                employees, and agents from and against any and all claims, liabilities, damages, losses, costs, and
                expenses (including reasonable attorneys&apos; fees) arising out of or related to:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Your use or misuse of the Platform</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any applicable law or regulation</li>
                <li>Any content you generate using the Platform</li>
                <li>Your infringement of any third-party rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">10. Termination</h2>
              <p>You may close your account at any time. Upon account closure:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Unused credits will not be refunded</li>
                <li>Access to previously generated audio files will be terminated</li>
                <li>Personal data will be handled in accordance with our Privacy Policy</li>
              </ul>
              <p className="mt-2">
                We also reserve the right to terminate any user account at any time, for any reason, with or
                without cause or notice. Provisions of these Terms that by their nature should survive termination
                shall survive, including but not limited to intellectual property, limitation of liability,
                indemnification, and governing law provisions.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">11. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of
                New Mexico, United States of America, without regard to its conflict of law principles. Any
                legal action or proceeding arising under these Terms shall be brought exclusively in the state
                or federal courts located in the State of New Mexico, and you hereby consent to the personal
                jurisdiction and venue of such courts.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">12. Dispute Resolution</h2>
              <p>
                Before initiating any legal proceeding, you agree to first attempt to resolve any dispute
                informally by contacting us at <a href="mailto:info@speakroar.com" className="text-cyan-600 hover:underline">info@speakroar.com</a>.
                We will attempt to resolve the dispute informally within thirty (30) days. If the dispute is not
                resolved within that period, either party may proceed with formal legal action as described in the
                Governing Law section above.
              </p>
              <p className="mt-2">
                To the fullest extent permitted by law, you agree that any dispute resolution proceedings will be
                conducted on an individual basis and not as part of a class, consolidated, or representative action.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">13. Severability</h2>
              <p>
                If any provision of these Terms is found to be invalid, illegal, or unenforceable by a court of
                competent jurisdiction, such provision shall be severed from these Terms, and the remaining
                provisions shall continue in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">14. Changes to Terms</h2>
              <p>
                We reserve the right to modify, update, or replace these Terms at any time at our sole discretion.
                Changes become effective immediately upon posting to the Platform. Your continued use of the
                Platform after any such changes constitutes your acceptance of the revised Terms. We encourage
                you to review these Terms periodically for updates.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">15. Contact</h2>
              <p>
                If you have any questions or concerns regarding these Terms of Service, please contact us at:
              </p>
              <p className="mt-2">
                <strong>CreateNano LLC</strong><br />
                1209 Mountain Road Pl NE, Ste N<br />
                Albuquerque, NM 87110, USA<br />
                Phone: <a href="tel:+15054052773" className="text-cyan-600 hover:underline">(505) 405-2773</a><br />
                Email: <a href="mailto:info@speakroar.com" className="text-cyan-600 hover:underline">info@speakroar.com</a>
              </p>
            </section>

          </div>
        </div>

        {/* Footer Links */}
        <div className="flex flex-wrap justify-center gap-4 mt-8 text-sm text-neutral-400">
          <Link href="/privacy" className="hover:text-cyan-600 transition-colors">Privacy Policy</Link>
          <span>·</span>
          <Link href="/cookies" className="hover:text-cyan-600 transition-colors">Cookie Policy</Link>
          <span>·</span>
          <Link href="/ccpa" className="hover:text-cyan-600 transition-colors">CCPA Notice</Link>
        </div>
      </div>
    </div>
  );
}
